// Action detail dialog — full lifecycle (submit → verify/reject), evidence
// upload/download/delete, activity timeline and comments. Extracted from
// ActionsPage to keep modules small.
import { useCallback, useEffect, useState } from "react";
import {
    X, Loader2, Pencil, Calendar, CheckCircle2, Send, ShieldCheck, RotateCcw,
    Upload, Download, Trash2, Paperclip, Clock, MessageSquare, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";

const PRIO_TONE = { Critical: "red", High: "red", Medium: "amber", Low: "blue" };
const STATUS_TONE = { "Open": "amber", "In progress": "blue", "Blocked": "red", "Submitted": "purple", "Complete": "green" };

const EVENT_META = {
    submitted: { color: "#6B4E9E", bg: "#EEE9F6", label: "Submitted for review", icon: Send },
    verified: { color: "#3F7D3A", bg: "#E8F1E6", label: "Verified & closed", icon: ShieldCheck },
    rejected: { color: "#B33A2E", bg: "#FBE8E5", label: "Returned for rework", icon: RotateCcw },
    reopened: { color: "#C68A1C", bg: "#FCF1DC", label: "Reopened", icon: RotateCcw },
    comment: { color: "#2A5DB0", bg: "#E7EEF9", label: "Comment", icon: MessageSquare },
    assigned: { color: "#6F6A64", bg: "#F0EDE8", label: "Assigned", icon: FileText },
};

function fmtDateTime(s) {
    if (!s) return "";
    try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return s; }
}

export default function ActionDetailDialog({ open, actionId, clients, canEdit, isReviewer, onEdit, onClose, onChanged }) {
    const [a, setA] = useState(null);
    const [busy, setBusy] = useState(false);
    const [comment, setComment] = useState("");
    const [rejecting, setRejecting] = useState(false);
    const [rejectNote, setRejectNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitNote, setSubmitNote] = useState("");
    const navigate = useNavigate();

    const load = useCallback(async () => {
        if (!actionId) return;
        try {
            const { data } = await api.get(`/actions/${actionId}`);
            // Form-assignment tasks (Actions/My Tasks entries created when a form
            // is assigned to someone) skip the action detail entirely — clicking
            // one takes the assignee straight to the form they need to complete.
            if (data.source === "Form" && data.source_ref) {
                onClose?.();
                navigate(`/app/inspections/${data.source_ref}`);
                return;
            }
            setA(data);
        } catch (e) {
            if (e.response?.status === 404) {
                toast.error("That action is no longer available");
                onClose?.();
            } else {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not load action");
            }
        }
    }, [actionId, onClose, navigate]);

    useEffect(() => { if (open) { setA(null); setRejecting(false); setRejectNote(""); setComment(""); load(); } }, [open, load]);

    if (!open) return null;
    const client = a ? clients.find((c) => c.id === a.client_id) : null;

    async function act(fn, okMsg) {
        setBusy(true);
        try { await fn(); toast.success(okMsg); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
        finally { setBusy(false); }
    }
    const submit = () => act(async () => {
        await api.post(`/actions/${a.id}/submit-review`, { note: submitNote.trim() || null });
        setSubmitting(false); setSubmitNote("");
    }, "Submitted for review");
    const verify = () => act(() => api.post(`/actions/${a.id}/verify`, {}), "Verified & closed");
    const markComplete = () => act(() => api.post(`/actions/${a.id}/complete`, {}), "Marked complete");
    const reject = () => {
        if (!rejectNote.trim()) { toast.error("Please add a reason"); return; }
        return act(async () => { await api.post(`/actions/${a.id}/reject`, { note: rejectNote }); setRejecting(false); setRejectNote(""); }, "Returned for rework");
    };
    const addComment = () => {
        if (!comment.trim()) return;
        return act(async () => { await api.post(`/actions/${a.id}/comment`, { note: comment }); setComment(""); }, "Comment added");
    };

    async function uploadEvidence(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        await act(() => api.post(`/actions/${a.id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } }), "Evidence uploaded");
        e.target.value = "";
    }
    async function deleteEvidence(eid) {
        await act(() => api.delete(`/actions/${a.id}/evidence/${eid}`), "Evidence removed");
    }
    function downloadEvidence(ev) {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/actions/${a.id}/evidence/${ev.id}`, "_blank");
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="action-detail-dialog"
                className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[680px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                {!a ? (
                    <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "#9A968F" }} /></div>
                ) : (
                    <>
                        <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Pill tone={STATUS_TONE[a.status] || "neutral"} data-testid="action-detail-status">{a.status === "Submitted" ? "Awaiting review" : a.status}</Pill>
                                    <Pill tone={PRIO_TONE[a.priority] || "neutral"}>{a.priority}</Pill>
                                    {a.ref && <span data-testid="action-detail-ref" className="font-mono text-[10.5px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FCF1DC", color: "#9F4212" }}>{a.ref}</span>}
                                    {client && <span className="text-[11px]" style={{ color: "#6F6A64" }}>{client.name}</span>}
                                </div>
                                <h2 className="font-display text-[19px] font-bold text-[#1F1F1F] mt-1.5">{a.title}</h2>
                                {a.description && <p className="text-[12.5px] mt-1" style={{ color: "#6F6A64" }}>{a.description}</p>}
                                <div className="flex items-center gap-3 mt-1.5 text-[11.5px] flex-wrap" style={{ color: "#9A968F" }}>
                                    <span data-testid="action-detail-assignee">Assignee: {a.assignee_name || "Unassigned"}</span>
                                    <span data-testid="action-detail-created-by">Raised by: {a.created_by_name || "—"}</span>
                                    {a.due_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.due_date}</span>}
                                </div>
                            </div>
                            <button data-testid="action-detail-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                        </div>

                        {a.status === "In progress" && a.review_notes && (
                            <div className="mx-6 mt-4 flex items-start gap-2 text-[12px] px-3 py-2.5 rounded-lg" style={{ background: "#FBE8E5", color: "#B33A2E" }}>
                                <RotateCcw className="w-4 h-4 mt-0.5 shrink-0" />
                                <div><b>Returned for rework:</b> {a.review_notes}</div>
                            </div>
                        )}

                        <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
                            <section data-testid="action-evidence-section">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Evidence ({a.evidence?.length || 0})</h3>
                                    {canEdit && (
                                        <label data-testid="action-evidence-upload" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[7px] text-[11.5px] font-semibold cursor-pointer border" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                            <Upload className="w-3.5 h-3.5" /> Upload
                                            <input type="file" className="hidden" onChange={uploadEvidence} disabled={busy} />
                                        </label>
                                    )}
                                </div>
                                {(a.evidence?.length || 0) === 0 ? (
                                    <p className="text-[12px] italic" style={{ color: "#9A968F" }}>No evidence uploaded yet.</p>
                                ) : (
                                    <ul className="space-y-1.5">
                                        {a.evidence.map((ev) => (
                                            <li key={ev.id} data-testid={`action-evidence-${ev.id}`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>
                                                <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[12px] font-medium text-[#1F1F1F] truncate">{ev.filename}</div>
                                                    <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{ev.caption ? ev.caption + " · " : ""}{Math.round((ev.size || 0) / 1024)} KB · {ev.uploaded_by_name || ""}</div>
                                                </div>
                                                <button data-testid={`action-evidence-download-${ev.id}`} onClick={() => downloadEvidence(ev)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0]" title="Download"><Download className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                                {canEdit && <button data-testid={`action-evidence-remove-${ev.id}`} onClick={() => deleteEvidence(ev.id)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBE8E5]" title="Remove"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>

                            <section data-testid="action-history-section">
                                <h3 className="text-[13px] font-semibold text-[#1F1F1F] mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Activity</h3>
                                {(a.review_history?.length || 0) === 0 ? (
                                    <p className="text-[12px] italic" style={{ color: "#9A968F" }}>No activity yet.</p>
                                ) : (
                                    <ul className="space-y-2.5">
                                        {[...a.review_history].reverse().map((h) => {
                                            const m = EVENT_META[h.event] || EVENT_META.comment;
                                            const Icon = m.icon;
                                            return (
                                                <li key={h.id} className="flex items-start gap-2.5">
                                                    <span className="w-6 h-6 rounded-full grid place-items-center shrink-0 mt-0.5" style={{ background: m.bg, color: m.color }}><Icon className="w-3 h-3" /></span>
                                                    <div className="min-w-0">
                                                        <div className="text-[12px] text-[#1F1F1F]"><b>{m.label}</b> · <span style={{ color: "#6F6A64" }}>{h.actor_name || "—"}</span></div>
                                                        {h.note && <div className="text-[11.5px] mt-0.5" style={{ color: "#6F6A64" }}>{h.note}</div>}
                                                        <div className="text-[10px] mt-0.5" style={{ color: "#9A968F" }}>{fmtDateTime(h.at)}</div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                {canEdit && (
                                    <div className="flex items-center gap-2 mt-3">
                                        <input data-testid="action-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…"
                                            className="flex-1 px-3 py-2 text-[12.5px] border rounded-lg outline-none" style={{ borderColor: "#E5E0DA" }} />
                                        <button data-testid="action-comment-send" onClick={addComment} disabled={busy || !comment.trim()}
                                            className="px-3 py-2 rounded-lg text-[12px] font-semibold border" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>Post</button>
                                    </div>
                                )}
                            </section>
                        </div>

                        {submitting && (
                            <div className="px-6 pb-3">
                                <textarea data-testid="action-submit-note" value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} rows={2}
                                    placeholder="Add a comment for the reviewer (optional)…"
                                    className="w-full px-3 py-2 text-[12.5px] border rounded-lg outline-none" style={{ borderColor: "#C9BEE0" }} />
                            </div>
                        )}

                        {rejecting && (
                            <div className="px-6 pb-3">
                                <textarea data-testid="action-reject-note" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} rows={2}
                                    placeholder="Reason for returning this action (required)…"
                                    className="w-full px-3 py-2 text-[12.5px] border rounded-lg outline-none" style={{ borderColor: "#E5A89E" }} />
                            </div>
                        )}

                        <div className="px-6 py-4 border-t flex items-center gap-2.5 flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                            {canEdit && <button data-testid="action-detail-edit" onClick={() => onEdit(a)} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}><Pencil className="w-3.5 h-3.5 inline mr-1" /> Edit</button>}
                            <div className="flex-1" />
                            {canEdit && ["Open", "In progress", "Blocked"].includes(a.status) && !submitting && (
                                <button data-testid="action-detail-complete" onClick={markComplete} disabled={busy}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "#3F7D3A" }}>
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark complete
                                </button>
                            )}
                            {canEdit && ["Open", "In progress", "Blocked"].includes(a.status) && !submitting && (
                                <button data-testid="action-detail-submit" onClick={() => setSubmitting(true)} disabled={busy}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "#6B4E9E" }}>
                                    <Send className="w-3.5 h-3.5" /> Submit for review
                                </button>
                            )}
                            {canEdit && ["Open", "In progress", "Blocked"].includes(a.status) && submitting && (
                                <>
                                    <button onClick={() => { setSubmitting(false); setSubmitNote(""); }} disabled={busy}
                                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                                    <button data-testid="action-submit-confirm" onClick={submit} disabled={busy}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "#6B4E9E" }}>
                                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Confirm submit
                                    </button>
                                </>
                            )}
                            {isReviewer && a.status === "Submitted" && !rejecting && (
                                <>
                                    <button data-testid="action-detail-reject" onClick={() => setRejecting(true)} disabled={busy}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5A89E", color: "#B33A2E" }}>
                                        <RotateCcw className="w-3.5 h-3.5" /> Return for rework
                                    </button>
                                    <button data-testid="action-detail-verify" onClick={verify} disabled={busy}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "#3F7D3A" }}>
                                        <ShieldCheck className="w-3.5 h-3.5" /> Verify &amp; close
                                    </button>
                                </>
                            )}
                            {isReviewer && a.status === "Submitted" && rejecting && (
                                <>
                                    <button onClick={() => { setRejecting(false); setRejectNote(""); }} disabled={busy}
                                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                                    <button data-testid="action-reject-confirm" onClick={reject} disabled={busy || !rejectNote.trim()}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "#B33A2E" }}>
                                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Confirm return
                                    </button>
                                </>
                            )}
                            {a.status === "Complete" && (
                                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#3F7D3A" }}>
                                    <CheckCircle2 className="w-4 h-4" /> Verified by {a.verified_by_name || "Priority Risk"}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
